import React, { useEffect, useState, useCallback } from "react"
import {
	Drawer,
	Box,
	IconButton,
	Typography,
	useTheme,
	useMediaQuery,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import * as hooks from "lib/hooks"

export interface SidebarProps {
	open: boolean
	onClose: () => void
	width?: number | string
	position?: "left" | "right"
	hideCloseButton?: boolean
	title?: React.ReactNode
	children: React.ReactNode
	pushContent?: boolean
	sx?: any
	mobileWidth?: string
	tabletWidth?: string
	desktopWidth?: string
}

export function Sidebar({
	open,
	onClose,
	width = 350,
	position = "right",
	hideCloseButton = false,
	title,
	children,
	pushContent = false,
	sx = {},
	mobileWidth = "79%",
	tabletWidth = "58%",
	desktopWidth = "50%",
}: SidebarProps) {
	const theme = useTheme()
	const materialColors = hooks.UseMaterialColors()

	const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
	const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"))
	const isDesktop = useMediaQuery(theme.breakpoints.up("md"))

	const getResponsiveWidth = useCallback(() => {
		return `${width}px`
	}, [width, isMobile, isTablet, isDesktop, mobileWidth, tabletWidth, desktopWidth])

	const [drawerWidth, setDrawerWidth] = useState(getResponsiveWidth())

	useEffect(() => {
		setDrawerWidth(getResponsiveWidth())
	}, [getResponsiveWidth])

	// Optional: push content when open
	useEffect(() => {
		if (pushContent && !isMobile) {
			const body = document.body
			body.style.transition = "margin 300ms ease"
			if (open) {
				if (position === "right") {
					body.style.marginRight = drawerWidth
					body.style.marginLeft = "0"
				} else {
					body.style.marginLeft = drawerWidth
					body.style.marginRight = "0"
				}
			} else {
				body.style.marginRight = "0"
				body.style.marginLeft = "0"
			}
			return () => {
				body.style.marginRight = "0"
				body.style.marginLeft = "0"
			}
		}
	}, [open, drawerWidth, pushContent, position, isMobile])

	return (
		<Drawer
			anchor={position}
			open={open}
			variant="persistent" // <- allows interaction outside the drawer
			PaperProps={{
				sx: {
					width: drawerWidth,
					backgroundColor:
						theme.palette.mode === "dark"
							? materialColors.surfaceDark[2]
							: materialColors.surfaceLight[2],
					boxShadow: theme.shadows[4],
					border: "none",
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
					pointerEvents: "auto", // <- allow interaction
					...sx,
				},
			}}
			hideBackdrop // <- disables modal-style backdrop (optional)
			ModalProps={{
				keepMounted: true, // Keeps drawer in DOM
				hideBackdrop: true, // <- critical: removes overlay and allows clicking outside
			}}
		>
			{/* Header */}
			{(title || !hideCloseButton) && (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						px: 2,
						py: 1,
						borderBottom: `1px solid ${theme.palette.divider}`,
						backgroundColor:
							theme.palette.mode === "dark"
								? materialColors.surfaceDark.containerHigh
								: materialColors.surfaceLight.containerHigh,
					}}
				>
					<Typography
						variant="h6"
						sx={{
							fontWeight: 500,
							fontFamily: '"Google Sans", "Roboto", sans-serif',
							color:
								theme.palette.mode === "dark"
									? materialColors.surfaceDark.onSurface
									: materialColors.surfaceLight.onSurface,
						}}
					>
						{title}
					</Typography>

					{!hideCloseButton && (
						<IconButton onClick={onClose}>
							<CloseIcon />
						</IconButton>
					)}
				</Box>
			)}

			{/* Content */}
			<Box
				sx={{
					flex: 1,
					overflowY: "auto",
					padding: 2,
					scrollbarWidth: "thin",
					scrollbarColor: theme.palette.divider + " transparent",
					"&::-webkit-scrollbar": {
						width: "8px",
					},
					"&::-webkit-scrollbar-thumb": {
						backgroundColor: theme.palette.divider,
						borderRadius: "8px",
					},
				}}
			>
				{children}
			</Box>
		</Drawer>
	)
}
